import React, { Component, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getExampleAction, validateExampleAdapter, validateLiveAdapter } from "./pageExperienceModel";
import { usePageResource } from "./usePageResource";
import "./PageExperience.css";

export function PageStatus({ title, message, onRetry, error = false, children }) {
  return <section className="PageExperienceStatus" role={error ? "alert" : "status"}>
    <span className="PageExperienceStatus__mark" aria-hidden="true">{error ? "!" : "·"}</span>
    <h2>{title}</h2>
    {message ? <p>{message}</p> : null}
    {onRetry ? <button type="button" onClick={onRetry}>Try again</button> : null}
    {children}
  </section>;
}

export function PageSkeleton({ visible = true, label = "Preparing your page" }) {
  return <div className="PageExperienceSkeleton" aria-busy="true" role="status" aria-label={label}>
    <span className="PageExperience__srOnly">{label}</span>
    <div className="PageExperienceSkeleton__shapes" aria-hidden="true" style={{ visibility: visible ? "visible" : "hidden" }}>
      <div className="PageExperienceSkeleton__heading" />
      <div className="PageExperienceSkeleton__line" />
      <div className="PageExperienceSkeleton__summary">{[0, 1, 2].map((id) => <div key={id} />)}</div>
      <div className="PageExperienceSkeleton__columns">{[0, 1].map((id) => <div key={id}>
        <span />{[0, 1, 2, 3].map((row) => <i key={row} />)}
      </div>)}</div>
    </div>
  </div>;
}

function usePageViewport(ref, bottomInset) {
  const [viewport, setViewport] = useState({ width: 0, height: 560, layout: "compact" });
  const measure = useCallback(() => {
    const host = ref.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.max(280, Math.floor(window.innerHeight - Math.max(0, rect.top) - bottomInset - 16));
    const layout = width < 680 ? "compact" : width < 1040 ? "medium" : "wide";
    setViewport((current) => current.width === width && current.height === height
      ? current : { width, height, layout });
  }, [ref, bottomInset]);
  // Also catch a parent banner moving the page without changing its width.
  useLayoutEffect(() => { measure(); });
  useLayoutEffect(() => {
    const host = ref.current;
    if (!host) return;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(host);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [ref, measure]);
  return viewport;
}

export function useDelayedPlaceholder(delay = 150) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(timer);
  }, [delay]);
  return visible;
}

class AdapterBoundary extends Component {
  state = { error: null, attempt: 0 };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) return <PageStatus error title="This page could not be displayed"
      message="Please try again." onRetry={() => this.setState((state) => ({ error: null, attempt: state.attempt + 1 }))} />;
    return <React.Fragment key={this.state.attempt}>{this.props.children}</React.Fragment>;
  }
}

function LiveContent({ adapter, identity, slow, viewport }) {
  const resource = adapter.useModel({ identity });
  const retained = useRef(null);
  const ready = resource?.status === "ready" || resource?.status === "empty";
  const capabilities = resource?.capabilities ?? retained.current?.capabilities ?? {};
  useLayoutEffect(() => {
    if (ready) retained.current = { data: resource.data, empty: resource.status === "empty", capabilities };
    else if (retained.current) retained.current.capabilities = capabilities;
  });
  if (!["loading", "ready", "empty", "error"].includes(resource?.status)) {
    throw new Error("Page controllers must report an explicit readiness status.");
  }
  const current = ready ? { data: resource.data, empty: resource.status === "empty" } : retained.current;
  if (!current) {
    if (resource.status === "error") return <PageStatus error title="Your page could not be loaded"
      message="Please try again to load your data." onRetry={resource.retry} />;
    return <PageSkeleton visible={slow} />;
  }
  const View = adapter.View;
  return <div data-page-state={resource.status === "loading" ? "refreshing" : resource.status === "error" ? "refresh-error" : current.empty ? "empty" : "ready"}>
    <div className="PageExperience__refreshSlot">
      {resource.status === "loading" ? <p className="PageExperience__notice" role="status">Updating…</p> : null}
      {resource.status === "error" ? <div className="PageExperience__notice" role="alert">
        Update failed.<span className="PageExperience__srOnly"> Showing the last loaded content.</span>
        <button type="button" onClick={resource.retry}>Try again</button>
      </div> : null}
    </div>
    <View model={current.data} empty={current.empty} mode="live" viewport={viewport}
      capabilities={capabilities} onAction={resource.onAction} />
  </div>;
}

function LiveLoader({ loadLive, identity, slow, viewport }) {
  const module = usePageResource(loadLive, { identity });
  if (module.status === "error") return <PageStatus error title="Your page could not be loaded"
    message="Please try again to open this page." onRetry={module.retry} />;
  if (module.status !== "ready") return <PageSkeleton visible={slow} />;
  const adapter = validateLiveAdapter(module.data);
  return <LiveContent adapter={adapter} identity={identity} slow={slow} viewport={viewport} />;
}

function ExampleLoader({ loadExample, identity, slow, viewport, access, onAccessAction, returnTo }) {
  const module = usePageResource(loadExample, { identity });
  const [notice, setNotice] = useState("");
  const action = getExampleAction(access.reason, access);
  if (module.status === "error") return <PageStatus error title="The example could not be loaded"
    message="Please try again to view the example." onRetry={module.retry} />;
  if (module.status !== "ready") return <PageSkeleton visible={slow} />;
  const { View, model } = validateExampleAdapter(module.data);
  return <section className="PageExperienceExample" data-page-state="example" aria-label="Example page"
    style={{ "--page-preview-height": viewport.height + "px" }}>
    <div className="PageExperienceExample__badge"><span aria-hidden="true">◇</span> Example · Fictional data</div>
    <div className="PageExperienceExample__body" tabIndex={0} aria-label="Scrollable example content">
      <View model={model} mode="example" viewport={viewport} capabilities={{}}
        onAction={() => setNotice("This is an example. Use the access button below to work with your own data.")} />
    </div>
    <div className="PageExperienceExample__actionBar" aria-label="Example access options">
      <div><strong>You’re viewing an example</strong><p>{action.description}</p>
        {notice ? <p className="PageExperience__notice" role="status">{notice}</p> : null}</div>
      <button type="button" onClick={() => onAccessAction?.({ ...action, returnTo })}>{action.label}</button>
    </div>
  </section>;
}

function ExperienceScope({ identity, access, loadLive, loadExample, onAccessAction, returnTo, bottomInset = 0, skeletonDelay = 150 }) {
  const ref = useRef(null);
  const viewport = usePageViewport(ref, bottomInset);
  const slow = useDelayedPlaceholder(skeletonDelay);
  let content;
  if (access.status === "error") content = <PageStatus error title="We couldn’t confirm your access"
    message="Please try again. This does not mean your access was denied." onRetry={access.retry} />;
  else if (access.status === "permitted") content = <LiveLoader
    loadLive={loadLive} identity={identity} slow={slow} viewport={viewport} />;
  else if (access.status === "denied" && loadExample) content = <ExampleLoader
    {...{ loadExample, identity, slow, viewport, access, onAccessAction, returnTo }} />;
  else if (access.status === "denied") {
    const action = getExampleAction(access.reason, access);
    content = <PageStatus title="Access needed" message={action.description}>
      <button type="button" onClick={() => onAccessAction?.({ ...action, returnTo })}>{action.label}</button>
    </PageStatus>;
  } else content = <PageSkeleton visible={slow} label="Checking access" />;
  return <div ref={ref} className="PageExperience" data-layout={viewport.layout}>
    <AdapterBoundary key={access.status}>{content}</AdapterBoundary>
  </div>;
}

/**
 * access: {status: pending|error|denied|permitted, reason?, retry?, canManageBilling?}
 * live: lazy {View, useModel}; useModel returns {status: loading|ready|empty|error,
 * data, retry, capabilities?, onAction?}. example: lazy {View, model}, no live hooks.
 * Neither adapter is mounted until access is resolved. No live view is hidden
 * behind a preview. A new identity, access revocation, or pause drops local data.
 */
export default function PageExperience({ identity, paused = false, pauseMessage, access = { status: "pending" }, ...props }) {
  if (paused) return <PageStatus title="Page paused" message={pauseMessage || "This page will resume when recording or export finishes."} />;
  return <ExperienceScope key={identity} identity={identity} access={access} {...props} />;
}
